import React, {useRef, useState} from "react";
import Papa from "papaparse";
import SoilController from "../../controllers/SoilController";
import {RenderTableStruct, SubmissionPrepareBase} from "../submission/SubmissionPrepareBase";
import PrepareSubmissionSoil from "../../controllers/PrepareSubmissionSoil";
import SubmissionDataResponse from "../../controllers/SubmissionDataResponse";
import {arrayUpdate} from "../../immutableState";
import Lab from "../../controllers/Lab";
import TestCodes from "../../TestCodes";
import {insertAt, split} from "../../array";
import SubmissionTable from "../itestsoil/SubmissionTable";
import {useGroupValidation} from "../validationArray";
import Dialog from "../../components/Dialog";
import SampleDepth from "../../controllers/SampleDepth";
import FileUploader from "../../components/FileUploader";
import {CSVPopulate, parseCSVFile} from "../CSVParse";
import {CheckCircleIcon, XCircleIcon, PlusCircleIcon} from "@heroicons/react/24/outline";
import {saveAs} from "file-saver";

export type SoilRow = PrepareSubmissionSoil & { selected?: boolean };

export function emptySoilRow(): SoilRow {
    return {
        testId: 0,
        testDataId: null,
        submissionId: null,
        blockNumber: "",
        sampleNumber: "",
        notes: "",
        delete: false,
        test: TestCodes.Unknown,
        sampleDepth: SampleDepth.TopSoil,
        agronomist: "",
        farmName: "",
        depot: "",
        clientName: "",
        agent: "",
        appAdded: new Date(),
        rawLab: "",
        optionalTests: [],
        selected: false,
        latitude: null,
        longitude: null,
    };
}

function stripTrademark(s: string): string {
    return s.replace(/™/g, '').toLowerCase().trim();
}

interface ImportSummary {
    matchedSamples: string[];
    unmatchedExistingSamples: string[];
    newCsvSamples: string[];
    mergedSplitData: SoilRow[][];
    setSplitData: (data: SoilRow[][]) => void;
}

function allSelectedOrUnselected(farmerRow: SoilRow[]) {
    return farmerRow.filter(s => s.selected).length > 0 && farmerRow.length !== farmerRow.filter(s => s.selected).length;
}

export default function SubmissionPrepareSoil(props: { dataIds: number[]; close: (saved: boolean) => void }) {
    const { register: registerValidation, validateAll } = useGroupValidation();

    const [showImportExport, setShowImportExport] = useState(false);
    const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);

    // Refs to access current data in export/import handlers (captured each render via renderTable)
    const splitDataRef = useRef<SoilRow[][]>([]);
    const setSplitDataRef = useRef<(data: SoilRow[][]) => void>(() => {});
    const submissionDataRef = useRef<SubmissionDataResponse<SoilRow> | null>(null);

    function buildCsvPopulateMap(submissionData: SubmissionDataResponse<SoilRow>): Record<string, CSVPopulate<SoilRow>> {
        return {
            'Latitude': (value, entry) => {
                entry.latitude = value === '' ? null : isNaN(parseFloat(value)) ? null : parseFloat(value);
            },
            'Longitude': (value, entry) => {
                entry.longitude = value === '' ? null : isNaN(parseFloat(value)) ? null : parseFloat(value);
            },
            'Sample Number': (value, entry) => {
                entry.sampleNumber = value.trim();
            },
            'Sample Depth': (value, entry) => {
                const match = submissionData.sampleDepths.find(
                    d => d.value.toLowerCase().trim() === value.toLowerCase().trim()
                );
                if (match) entry.sampleDepth = match.key;
            },
            'Block': (value, entry) => {
                entry.blockNumber = value;
            },
            'Tests': (value, entry) => {
                if (!value.trim()) return;
                const stripped = stripTrademark(value);
                const test = submissionData.tests.find(t => stripTrademark(t.value) === stripped);
                if (test) entry.test = test.key;
            },
            'Optional Tests': (value, entry) => {
                if (!value.trim()) return;
                const parts = value.split(',').map(s => stripTrademark(s)).filter(s => s.length > 0);
                entry.optionalTests = submissionData.optionalTests
                    .filter(t => parts.includes(stripTrademark(t.value)))
                    .map(t => t.key);
            },
        };
    }

    function computeMerge(csvRows: SoilRow[], existingRows: SoilRow[]) {
        const mergedRows = existingRows.map(r => ({...r}));
        const matchedSamples: string[] = [];
        const newCsvSamples: string[] = [];
        const newRows: SoilRow[] = [];
        const matchedExistingSet = new Set<string>();

        for (const csvRow of csvRows) {
            if (!csvRow.sampleNumber.trim()) continue;

            const existingIndex = mergedRows.findIndex(
                r => r.sampleNumber === csvRow.sampleNumber
            );

            if (existingIndex >= 0) {
                mergedRows[existingIndex] = {
                    ...mergedRows[existingIndex],
                    blockNumber: csvRow.blockNumber,
                    sampleDepth: csvRow.sampleDepth,
                    test: csvRow.test,
                    optionalTests: csvRow.optionalTests,
                } as SoilRow;
                matchedSamples.push(csvRow.sampleNumber);
                matchedExistingSet.add(csvRow.sampleNumber);
            } else {
                newCsvSamples.push(csvRow.sampleNumber);
                newRows.push(csvRow);
            }
        }

        const unmatchedExistingSamples = existingRows
            .filter(r => r.sampleNumber && !matchedExistingSet.has(r.sampleNumber))
            .map(r => r.sampleNumber);

        const unmatchedSet = new Set(unmatchedExistingSamples);
        const finalRows = mergedRows.map(r => ({
            ...r,
            selected: unmatchedSet.has(r.sampleNumber),
        }));

        return {
            matchedSamples,
            unmatchedExistingSamples,
            newCsvSamples,
            mergedRows: [...finalRows, ...newRows]
        };
    }

    function handleImportFile(file: File) {
        const submissionData = submissionDataRef.current;
        if (!submissionData) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const csvRows = parseCSVFile(text, emptySoilRow, buildCsvPopulateMap(submissionData));

            const splitData = splitDataRef.current;
            const allExistingRows = splitData.flat();
            const merge = computeMerge(csvRows, allExistingRows);

            // Reconstruct groups from merged result
            const mergedSplitData: SoilRow[][] = [];
            let offset = 0;
            for (const group of splitData) {
                mergedSplitData.push(merge.mergedRows.slice(offset, offset + group.length));
                offset += group.length;
            }
            // New rows (unmatched) go into group 0
            const newRows = merge.mergedRows.slice(offset);
            if (newRows.length > 0 && mergedSplitData[0]) {
                mergedSplitData[0] = [...mergedSplitData[0], ...newRows];
            }

            setImportSummary({
                matchedSamples: merge.matchedSamples,
                unmatchedExistingSamples: merge.unmatchedExistingSamples,
                newCsvSamples: merge.newCsvSamples,
                mergedSplitData,
                setSplitData: setSplitDataRef.current,
            });
            setShowImportExport(false);
        };
        reader.readAsText(file);
    }

    function handleContinue() {
        if (!importSummary) return;
        importSummary.setSplitData(importSummary.mergedSplitData);
        setImportSummary(null);
    }

    function handleAbort() {
        setImportSummary(null);
    }

    function handleExportCsv() {
        const submissionData = submissionDataRef.current;
        if (!submissionData) return;

        const allRows = splitDataRef.current.flat();

        const csvData = allRows
            .filter(r => !r.delete)
            .map(row => ({
                'LATITUDE': row.latitude ?? '',
                'LONGITUDE': row.longitude ?? '',
                'SAMPLE NUMBER': row.sampleNumber,
                'SAMPLE DEPTH': submissionData.sampleDepths.find(d => d.key === row.sampleDepth)?.value ?? '',
                'BLOCK': row.blockNumber,
                'TESTS': submissionData.tests.find(t => t.key === row.test)?.value ?? '',
                'OPTIONAL TESTS': (row.optionalTests ?? [])
                    .map(ot => submissionData.optionalTests.find(t => t.key === ot)?.value ?? '')
                    .filter(v => v)
                    .join(', '),
            }));

        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], {type: 'text/csv'});
        saveAs(blob, 'SubmissionSoilExport.csv');
        setShowImportExport(false);
    }

    function renderTable(submissionData: SubmissionDataResponse<SoilRow>, data: SoilRow[][], setData: (newData: SoilRow[][]) => void, setShowUpload: (show: { show: boolean, index: number }) => void): RenderTableStruct {
        // Keep refs fresh for export/import handlers
        splitDataRef.current = data;
        setSplitDataRef.current = setData;
        submissionDataRef.current = submissionData;

        function addRow(index: number) {
            const newArray = data.map((c, i) => index !== i ? c : c.concat(emptySoilRow()));
            setData(newArray);
        }

        let result =
            <>
                {data.map((rowArr, index) => (
                    <div key={index}>
                        <SubmissionTable index={index} data={submissionData} leafs={rowArr} registerValidation={registerValidation}
                                         setData={d => {
                                             setData(arrayUpdate(data, index, d));
                                         }}/>

                        <div className='flex justify-between p-1 mb-1 pb-4 border-b border-gray-400'>
                            <div className='flex items-center'>
                                <div className='btn bg-primary-500' onClick={() => addRow(index)}>+</div>
                            </div>

                            {allSelectedOrUnselected(rowArr)
                                ? <div className='btn bg-primary' onClick={() => {
                                    const part = split(rowArr, d => d.selected ?? false);
                                    const mutate = data.map(d => d);
                                    mutate.splice(index, 1);
                                    insertAt(mutate, index, part.one.map(d => ({...d, selected: false})), part.two);
                                    setData(mutate);
                                }}>Split</div>
                                : null
                            }
                        </div>
                    </div>
                ))}
            </>;

        return {
            render: result,
            validation: () => validateAll()
        };
    }

    return (
        <div>
            <SubmissionPrepareBase
                labs={[Lab.ManualSoil, Lab.NvirotekSoil, Lab.WaypointSoil]}
                testDataIds={props.dataIds}
                close={props.close}
                prepare={SoilController.prepareSubmission}
                submit={SoilController.submit}
                renderTable={renderTable}
                csvPopulateMap={{}}
                emptyRow={emptySoilRow}
                headerFields={[
                    {
                        label: 'Import/Export CSV',
                        value: (
                            <div className="btn bg-primary-500" onClick={() => setShowImportExport(true)}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                     strokeWidth="1.5" stroke="currentColor" className="w-6 h-6">
                                    <path strokeLinecap="round" strokeLinejoin="round"
                                          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/>
                                </svg>
                            </div>
                        )
                    }
                ]}
            />
            <Dialog title="Import/Export CSV" show={showImportExport}
                    setShow={v => setShowImportExport(v as boolean)} body={
                <div className="p-4">
                    <div>Export current session CSV, update data and upload again.</div>
                    <a className="underline cursor-pointer" onClick={handleExportCsv}>
                        SubmissionSoilExport.csv
                    </a>
                    <div className="my-2">
                        <FileUploader onChange={handleImportFile} fileTypes=".csv"/>
                    </div>
                </div>
            }/>

            {importSummary && (
                <Dialog title="Import Summary" show={true}
                        setShow={() => handleAbort()}>
                    <div className="p-2 min-w-[400px]">
                        <div className="pb-1 font-bold font-sm">Matched:</div>
                        <div className="flex items-center text-xs text-wrap">
                            <CheckCircleIcon className="h-5 w-5 text-primary" aria-hidden="true"/>
                            <div>{importSummary.matchedSamples.length} sample(s) matched successfully</div>
                        </div>

                        {importSummary.unmatchedExistingSamples.length > 0 && (
                            <>
                                <div className="py-1 font-bold font-sm">Not in CSV:</div>
                                {importSummary.unmatchedExistingSamples.map((s, i) => (
                                    <div key={i} className="flex items-center text-xs text-wrap">
                                        <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true"/>
                                        <div>{s}</div>
                                    </div>
                                ))}
                            </>
                        )}

                        {importSummary.newCsvSamples.length > 0 && (
                            <>
                                <div className="py-1 font-bold font-sm">Added:</div>
                                {importSummary.newCsvSamples.map((s, i) => (
                                    <div key={i} className="flex items-center text-xs text-wrap">
                                        <PlusCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true"/>
                                        <div>{s}</div>
                                    </div>
                                ))}
                            </>
                        )}

                        <div className="flex justify-end gap-2 mt-4">
                            <div className="btn bg-red-500" onClick={handleAbort}>Abort</div>
                            <div className="btn bg-primary-500" onClick={handleContinue}>Continue</div>
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
}
